import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { getSupabaseClient } from '../../lib/supabase';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  Key, 
  Smartphone, 
  Mail, 
  CheckCircle2, 
  XCircle, 
  Copy, 
  Share2, 
  Loader2, 
  AlertCircle, 
  RefreshCw,
  Clock,
  ExternalLink,
  ShieldAlert,
  X
} from 'lucide-react';

export interface ProfessionalLoginInfo {
  has_login: boolean;
  login_id?: string | null;
  login_email?: string | null;
  login_active?: boolean | null;
  last_sign_in_at?: string | null;
}

export interface ProfessionalLoginCardProps {
  tenantId: string;
  professional: {
    id: string;
    full_name: string;
    phone?: string | null;
    service_provider_id?: string | null;
  };
  onLoginCreated?: () => void;
}

export function ProfessionalLoginCard({
  tenantId,
  professional,
  onLoginCreated
}: ProfessionalLoginCardProps) {
  const supabase = getSupabaseClient();

  const [loading, setLoading] = useState(true);
  const [loginInfo, setLoginInfo] = useState<ProfessionalLoginInfo | null>(null);

  // Form mode for creating login: 'user_id' | 'email'
  const [createMode, setCreateMode] = useState<'user_id' | 'email'>('user_id');
  const [emailInput, setEmailInput] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Success modal state (Password is ONLY available once)
  const [credentialsModal, setCredentialsModal] = useState<{
    loginIdOrEmail: string;
    password: string;
    loginUrl: string;
  } | null>(null);

  // Confirm dialogs
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmToggleActive, setConfirmToggleActive] = useState<boolean | null>(null);

  // ----------------------------------------------------
  // Fetch Login Info via RPC
  // ----------------------------------------------------
  const fetchLoginInfo = useCallback(async () => {
    if (!supabase || !professional?.id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_professional_login_info', {
        p_professional_id: professional.id
      });

      if (error) {
        throw error;
      }

      if (data && data.length > 0) {
        setLoginInfo(data[0] as ProfessionalLoginInfo);
      } else {
        setLoginInfo({ has_login: false });
      }
    } catch (err: any) {
      console.error('Error fetching professional login info:', err);
      // Surface exact backend error
      toast.error(err.message || 'Failed to fetch login status');
    } finally {
      setLoading(false);
    }
  }, [supabase, professional?.id]);

  useEffect(() => {
    fetchLoginInfo();
  }, [fetchLoginInfo]);

  // ----------------------------------------------------
  // 1. Create Login
  // ----------------------------------------------------
  const handleCreateLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase) return;

    if (!professional.phone) {
      toast.error('Pehle professional ka phone number save karein.');
      return;
    }

    if (createMode === 'email' && !emailInput.trim()) {
      toast.error('Email address zaroori hai');
      return;
    }

    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('manage-service-professional-login', {
        body: {
          action: 'create',
          tenant_id: tenantId,
          professional_id: professional.id,
          mode: createMode,
          email: createMode === 'email' ? emailInput.trim() : undefined
        }
      });

      if (error) {
        let msg = error.message;
        try {
          if (error.context && typeof error.context.json === 'function') {
            const errBody = await error.context.json();
            if (errBody?.error) msg = errBody.error;
          }
        } catch (_) {}
        throw new Error(msg);
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      const loginUrl = `${window.location.origin}/professional/login`;
      const loginIdOrEmail = data.login_id || data.login_email || (createMode === 'email' ? emailInput.trim() : 'User ID');

      setCredentialsModal({
        loginIdOrEmail,
        password: data.password,
        loginUrl
      });

      toast.success('App access successfully created!');
      await fetchLoginInfo();
      onLoginCreated?.();
    } catch (err: any) {
      console.error('Create login error:', err);
      toast.error(err.message || 'Failed to create login');
    } finally {
      setSubmitting(false);
    }
  };

  // ----------------------------------------------------
  // 2. Reset Password
  // ----------------------------------------------------
  const handleResetPassword = async () => {
    if (!supabase) return;
    setSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke('manage-service-professional-login', {
        body: {
          action: 'reset_password',
          tenant_id: tenantId,
          professional_id: professional.id
        }
      });

      if (error) {
        let msg = error.message;
        try {
          if (error.context && typeof error.context.json === 'function') {
            const errBody = await error.context.json();
            if (errBody?.error) msg = errBody.error;
          }
        } catch (_) {}
        throw new Error(msg);
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      const loginUrl = `${window.location.origin}/professional/login`;
      const loginIdOrEmail = loginInfo?.login_id || loginInfo?.login_email || 'User ID';

      setCredentialsModal({
        loginIdOrEmail,
        password: data.password,
        loginUrl
      });

      setConfirmReset(false);
      toast.success('Password reset successfully!');
      await fetchLoginInfo();
    } catch (err: any) {
      console.error('Reset password error:', err);
      toast.error(err.message || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  // ----------------------------------------------------
  // 3. Toggle Active / Disabled Login
  // ----------------------------------------------------
  const handleToggleActive = async (targetActive: boolean) => {
    if (!supabase) return;
    setSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke('manage-service-professional-login', {
        body: {
          action: 'set_active',
          tenant_id: tenantId,
          professional_id: professional.id,
          active: targetActive
        }
      });

      if (error) {
        let msg = error.message;
        try {
          if (error.context && typeof error.context.json === 'function') {
            const errBody = await error.context.json();
            if (errBody?.error) msg = errBody.error;
          }
        } catch (_) {}
        throw new Error(msg);
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      toast.success(targetActive ? 'Login access enabled!' : 'Login access disabled!');
      setConfirmToggleActive(null);
      await fetchLoginInfo();
    } catch (err: any) {
      console.error('Toggle active error:', err);
      toast.error(err.message || 'Failed to update login status');
    } finally {
      setSubmitting(false);
    }
  };

  // Copy helper
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  if (loading) {
    return (
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 text-slate-400 text-xs flex items-center justify-center gap-2">
        <Loader2 size={14} className="animate-spin text-primary" />
        <span>Checking partner app login status...</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* ======================================================== */}
      {/* CASE 1: PROFESSIONAL HAS NO LOGIN YET                    */}
      {/* ======================================================== */}
      {!loginInfo?.has_login && (
        <Card className="bg-slate-800/80 border-slate-700 rounded-2xl p-4 text-white space-y-3">
          <div className="flex items-center justify-between pb-2 border-b border-slate-700/60">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
              <Key size={14} className="text-primary" />
              <span>Partner App Login</span>
            </span>
            <span className="text-[10px] font-semibold text-amber-300 bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20">
              Not Enabled
            </span>
          </div>

          <p className="text-xs text-slate-300 leading-relaxed">
            Give <strong>{professional.full_name}</strong> access to the Service Professional App to receive job notifications, verify OTPs, and track earnings.
          </p>

          {!professional.phone ? (
            <div className="p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs flex items-center gap-2">
              <AlertCircle size={15} className="shrink-0 text-amber-400" />
              <span>Pehle professional ka phone number save karein.</span>
            </div>
          ) : null}

          <form onSubmit={handleCreateLogin} className="space-y-3 pt-1">
            <div className="space-y-2">
              <label className="text-[11px] font-semibold text-slate-300 block">
                Login Type
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                <label className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-colors ${
                  createMode === 'user_id'
                    ? 'bg-primary/10 border-primary text-white'
                    : 'bg-slate-900/60 border-slate-700 text-slate-400 hover:text-slate-200'
                }`}>
                  <input
                    type="radio"
                    name="createMode"
                    value="user_id"
                    checked={createMode === 'user_id'}
                    onChange={() => setCreateMode('user_id')}
                    className="mt-0.5"
                  />
                  <div>
                    <span className="font-semibold text-white block">User ID + Password</span>
                    <span className="text-[10px] text-slate-400">Recommended. Auto-generates a simple partner ID (e.g. BBP0012).</span>
                  </div>
                </label>

                <label className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-colors ${
                  createMode === 'email'
                    ? 'bg-primary/10 border-primary text-white'
                    : 'bg-slate-900/60 border-slate-700 text-slate-400 hover:text-slate-200'
                }`}>
                  <input
                    type="radio"
                    name="createMode"
                    value="email"
                    checked={createMode === 'email'}
                    onChange={() => setCreateMode('email')}
                    className="mt-0.5"
                  />
                  <div>
                    <span className="font-semibold text-white block">Email se login</span>
                    <span className="text-[10px] text-slate-400">Uses professional's personal email address.</span>
                  </div>
                </label>
              </div>
            </div>

            {createMode === 'email' && (
              <div>
                <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                  Professional's Email *
                </label>
                <Input
                  type="email"
                  value={emailInput}
                  onChange={e => setEmailInput(e.target.value)}
                  placeholder="e.g. professional@example.com"
                  required
                  className="h-9 bg-slate-900 border-slate-700 text-white text-xs rounded-xl"
                />
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting || !professional.phone}
              className="w-full bg-primary hover:bg-primary/90 text-white text-xs h-9 rounded-xl font-bold flex items-center justify-center gap-1.5"
            >
              {submitting ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Key size={13} />
              )}
              <span>Create App Login</span>
            </Button>
          </form>
        </Card>
      )}

      {/* ======================================================== */}
      {/* CASE 2: PROFESSIONAL ALREADY HAS A LOGIN                 */}
      {/* ======================================================== */}
      {loginInfo?.has_login && (
        <Card className="bg-slate-800/80 border-slate-700 rounded-2xl p-4 text-white space-y-3.5">
          <div className="flex items-center justify-between pb-2 border-b border-slate-700/60">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
              <Key size={14} className="text-primary" />
              <span>Partner App Login</span>
            </span>
            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
              loginInfo.login_active !== false
                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                : 'bg-rose-500/20 text-rose-300 border border-rose-500/30'
            }`}>
              {loginInfo.login_active !== false ? 'Active' : 'Disabled'}
            </span>
          </div>

          <div className="p-3 bg-slate-900/80 rounded-xl border border-slate-700/60 space-y-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-slate-400">Login ID / Username:</span>
              <span className="font-mono font-bold text-white">
                {loginInfo.login_id || loginInfo.login_email || '—'}
              </span>
            </div>

            {loginInfo.login_id && loginInfo.login_email && (
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-slate-400">Internal Auth Email:</span>
                <span className="font-mono text-slate-400">{loginInfo.login_email}</span>
              </div>
            )}

            <div className="flex items-center justify-between pt-1 border-t border-slate-800 text-[11px]">
              <span className="text-slate-400 flex items-center gap-1">
                <Clock size={11} />
                <span>Last Sign-In:</span>
              </span>
              <span className="text-slate-300">
                {loginInfo.last_sign_in_at
                  ? new Date(loginInfo.last_sign_in_at).toLocaleString()
                  : 'Never signed in yet'}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setConfirmReset(true)}
              disabled={submitting}
              className="border-slate-700 text-slate-200 hover:bg-slate-700 text-xs h-8 rounded-xl font-medium"
            >
              <RefreshCw size={12} className="mr-1 text-primary" />
              <span>Reset Password</span>
            </Button>

            {loginInfo.login_active !== false ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setConfirmToggleActive(false)}
                disabled={submitting}
                className="border-rose-700/50 text-rose-400 hover:bg-rose-950/40 text-xs h-8 rounded-xl font-medium"
              >
                <XCircle size={12} className="mr-1" />
                <span>Disable Login</span>
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setConfirmToggleActive(true)}
                disabled={submitting}
                className="border-emerald-700/50 text-emerald-400 hover:bg-emerald-950/40 text-xs h-8 rounded-xl font-medium"
              >
                <CheckCircle2 size={12} className="mr-1" />
                <span>Enable Login</span>
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* ======================================================== */}
      {/* CONFIRM RESET PASSWORD DIALOG                            */}
      {/* ======================================================== */}
      {confirmReset && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-700 w-full max-w-sm rounded-2xl shadow-2xl p-5 animate-in zoom-in-95 duration-150 text-white space-y-3">
            <h4 className="text-sm font-bold">Reset Partner Password?</h4>
            <p className="text-xs text-slate-300 leading-relaxed">
              This will immediately generate a new temporary password for <strong>{professional.full_name}</strong>. Their old password will stop working.
            </p>
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmReset(false)}
                disabled={submitting}
                className="flex-1 border-slate-700 text-slate-300 text-xs h-9 rounded-xl"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleResetPassword}
                disabled={submitting}
                className="flex-1 bg-primary hover:bg-primary/90 text-white text-xs h-9 rounded-xl font-semibold"
              >
                {submitting ? <Loader2 size={13} className="animate-spin" /> : 'Yes, Reset'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* CONFIRM DISABLE / ENABLE LOGIN DIALOG                     */}
      {/* ======================================================== */}
      {confirmToggleActive !== null && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-700 w-full max-w-sm rounded-2xl shadow-2xl p-5 animate-in zoom-in-95 duration-150 text-white space-y-3">
            <h4 className="text-sm font-bold">
              {confirmToggleActive ? 'Enable Login Access?' : 'Disable Login Access?'}
            </h4>
            <p className="text-xs text-slate-300 leading-relaxed">
              {confirmToggleActive
                ? `Allow ${professional.full_name} to sign in to the Professional App again.`
                : `Block ${professional.full_name} from signing in. (Note: Khula session 1 ghante tak chal sakta hai).`}
            </p>
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmToggleActive(null)}
                disabled={submitting}
                className="flex-1 border-slate-700 text-slate-300 text-xs h-9 rounded-xl"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => handleToggleActive(confirmToggleActive)}
                disabled={submitting}
                className={`flex-1 text-white text-xs h-9 rounded-xl font-semibold ${
                  confirmToggleActive
                    ? 'bg-emerald-600 hover:bg-emerald-700'
                    : 'bg-rose-600 hover:bg-rose-700'
                }`}
              >
                {submitting ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : confirmToggleActive ? (
                  'Enable Login'
                ) : (
                  'Disable Login'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* NEW CREDENTIALS MODAL (Shown ONLY once!)                 */}
      {/* ======================================================== */}
      {credentialsModal && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-700 w-full max-w-md rounded-2xl shadow-2xl p-5 sm:p-6 animate-in zoom-in-95 duration-150 text-white space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-1.5 text-emerald-400 font-bold text-sm">
                  <CheckCircle2 size={16} />
                  <span>Partner App Credentials Generated</span>
                </div>
                <p className="text-xs text-slate-400 mt-0.5">
                  Share these credentials with {professional.full_name}
                </p>
              </div>
              <button
                onClick={() => setCredentialsModal(null)}
                className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800"
              >
                <X size={16} />
              </button>
            </div>

            {/* Warning Banner */}
            <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs flex items-start gap-2">
              <ShieldAlert size={16} className="shrink-0 text-amber-400 mt-0.5" />
              <div>
                <strong>Yeh password dobara nahi dikhega.</strong>
                <p className="text-[11px] text-amber-200/80 mt-0.5">
                  Please copy or share this password right now. Once closed, you can only reset it to a new one.
                </p>
              </div>
            </div>

            {/* Credentials Card */}
            <div className="p-4 bg-slate-950 rounded-xl border border-slate-800 space-y-3 text-xs">
              <div>
                <span className="text-[10px] uppercase font-bold text-slate-500 block mb-1">
                  Login URL
                </span>
                <div className="flex items-center justify-between gap-2 p-2 bg-slate-900 rounded-lg border border-slate-800">
                  <span className="font-mono text-slate-300 truncate text-[11px]">
                    {credentialsModal.loginUrl}
                  </span>
                  <button
                    onClick={() => copyToClipboard(credentialsModal.loginUrl, 'Login URL')}
                    className="p-1 rounded text-slate-400 hover:text-white hover:bg-slate-800 shrink-0"
                    title="Copy URL"
                  >
                    <Copy size={13} />
                  </button>
                </div>
              </div>

              <div>
                <span className="text-[10px] uppercase font-bold text-slate-500 block mb-1">
                  User ID / Email
                </span>
                <div className="flex items-center justify-between gap-2 p-2 bg-slate-900 rounded-lg border border-slate-800">
                  <span className="font-mono font-bold text-white text-sm">
                    {credentialsModal.loginIdOrEmail}
                  </span>
                  <button
                    onClick={() => copyToClipboard(credentialsModal.loginIdOrEmail, 'User ID')}
                    className="p-1 rounded text-slate-400 hover:text-white hover:bg-slate-800 shrink-0"
                    title="Copy ID"
                  >
                    <Copy size={13} />
                  </button>
                </div>
              </div>

              <div>
                <span className="text-[10px] uppercase font-bold text-slate-500 block mb-1">
                  Password
                </span>
                <div className="flex items-center justify-between gap-2 p-2 bg-slate-900 rounded-lg border border-slate-800">
                  <span className="font-mono font-bold text-emerald-400 text-sm tracking-wider">
                    {credentialsModal.password}
                  </span>
                  <button
                    onClick={() => copyToClipboard(credentialsModal.password, 'Password')}
                    className="p-1 rounded text-slate-400 hover:text-white hover:bg-slate-800 shrink-0"
                    title="Copy Password"
                  >
                    <Copy size={13} />
                  </button>
                </div>
              </div>
            </div>

            {/* Share via WhatsApp & Done */}
            <div className="flex flex-col sm:flex-row gap-2 pt-1">
              <a
                href={`https://wa.me/?text=${encodeURIComponent(
                  `Namaste ${professional.full_name}, yahan aapke BahiBox Service Partner app ka login hai:\n\n🔗 Login Link: ${credentialsModal.loginUrl}\n👤 User ID: ${credentialsModal.loginIdOrEmail}\n🔑 Password: ${credentialsModal.password}\n\nApne phone browser mein khol kar login karein.`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-9 font-semibold rounded-xl inline-flex items-center justify-center gap-1.5 shadow"
              >
                <Share2 size={13} />
                <span>WhatsApp pe bhejein</span>
              </a>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setCredentialsModal(null)}
                className="border-slate-700 text-slate-300 text-xs h-9 rounded-xl"
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
export default ProfessionalLoginCard;
